// Advanced Training Plan Generation Utilities

import { Timestamp } from 'firebase/firestore';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { estimateWorkoutTSS, calculateCTL, calculateATL, calculateTSB } from './tssCalculator';
import { WORKOUT_TYPES, PRIORITY_LEVELS } from './trainingTypes';
import {
  calculatePowerZonesFromFTP,
  calculateHRZonesFromMax,
  estimateMaxHRFromAge,
  estimateFTPFromMaxPower,
} from './zoneCalculator';
import { db } from '../firebase';

/**
 * Calculate weekly TSS target based on hours, current fitness, and phase
 */
export function calculateWeeklyTSS(weeklyHours, currentCTL = 50, phaseType = 'base') {
  // Base TSS per hour varies by phase
  const baseTSSPerHour = {
    base: 50,      // Base phase: moderate intensity
    build: 60,     // Build phase: higher intensity
    peak: 70,      // Peak phase: high intensity
    recovery: 30   // Recovery: low intensity
  };

  const tssPerHour = baseTSSPerHour[phaseType] || 50;
  const targetTSS = weeklyHours * tssPerHour;

  // Don't increase too quickly (max 10% per week)
  const maxIncrease = currentCTL * 0.1;
  const adjustedTSS = Math.min(targetTSS, currentCTL + maxIncrease);

  return Math.round(adjustedTSS);
}

/**
 * Determine training phases for a plan period
 */
export function determinePhases(startDate, endDate, periodization = 'linear') {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.ceil(totalDays / 7);

  const phases = [];

  if (periodization === 'linear') {
    // Base (60%) -> Build (30%) -> Peak (10%)
    const baseWeeks = Math.floor(totalWeeks * 0.6);
    const buildWeeks = Math.floor(totalWeeks * 0.3);
    const peakWeeks = totalWeeks - baseWeeks - buildWeeks;

    let currentWeek = 0;
    if (baseWeeks > 0) {
      phases.push({ type: 'base', startWeek: currentWeek, weeks: baseWeeks });
      currentWeek += baseWeeks;
    }
    if (buildWeeks > 0) {
      phases.push({ type: 'build', startWeek: currentWeek, weeks: buildWeeks });
      currentWeek += buildWeeks;
    }
    if (peakWeeks > 0) {
      phases.push({ type: 'peak', startWeek: currentWeek, weeks: peakWeeks });
    }
  } else if (periodization === 'reverse') {
    // Start high, taper down
    const peakWeeks = Math.floor(totalWeeks * 0.3);
    const buildWeeks = Math.floor(totalWeeks * 0.4);
    const baseWeeks = totalWeeks - peakWeeks - buildWeeks;

    let currentWeek = 0;
    if (peakWeeks > 0) {
      phases.push({ type: 'peak', startWeek: currentWeek, weeks: peakWeeks });
      currentWeek += peakWeeks;
    }
    if (buildWeeks > 0) {
      phases.push({ type: 'build', startWeek: currentWeek, weeks: buildWeeks });
      currentWeek += buildWeeks;
    }
    if (baseWeeks > 0) {
      phases.push({ type: 'base', startWeek: currentWeek, weeks: baseWeeks });
    }
  } else if (periodization === 'block') {
    // 3-week blocks: 2 weeks build, 1 week recovery
    const blockWeeks = 3;
    const numBlocks = Math.floor(totalWeeks / blockWeeks);
    let currentWeek = 0;

    for (let i = 0; i < numBlocks; i++) {
      phases.push({ type: 'build', startWeek: currentWeek, weeks: 2 });
      currentWeek += 2;
      phases.push({ type: 'recovery', startWeek: currentWeek, weeks: 1 });
      currentWeek += 1;
    }

    // Remaining weeks
    const remaining = totalWeeks - currentWeek;
    if (remaining > 0) {
      phases.push({ type: 'base', startWeek: currentWeek, weeks: remaining });
    }
  } else {
    // Undulating: alternate between phases
    let currentWeek = 0;
    let phaseIndex = 0;
    const phaseTypes = ['base', 'build', 'base', 'build', 'peak'];
    
    while (currentWeek < totalWeeks) {
      const weeks = Math.min(2, totalWeeks - currentWeek); // 2-week cycles
      phases.push({ type: phaseTypes[phaseIndex % phaseTypes.length], startWeek: currentWeek, weeks });
      currentWeek += weeks;
      phaseIndex++;
    }
  }

  return phases;
}

/**
 * Distribute workouts for polarized training (80/20) - Enhanced
 * True 80/20 with clear separation: very easy long rides + very hard short intervals
 */
export function distributePolarizedWorkouts(weeklyTSS, weeklyHours) {
  const easyTSS = weeklyTSS * 0.8;
  const hardTSS = weeklyTSS * 0.2;

  const workouts = [];

  // 80% Easy: Multiple long, easy sessions
  // Long endurance ride (Z1-Z2) - 50% of total time
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.50 * 3600,
    intensity: 1.5, // Very easy
    tss: easyTSS * 0.40,
  });

  // Medium endurance (Z2) - 20% of total time
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.20 * 3600,
    intensity: 2,
    tss: easyTSS * 0.35,
  });

  // Recovery (Z1) - 10% of total time
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.10 * 3600,
    intensity: 1,
    tss: easyTSS * 0.25,
  });

  // 20% Hard: Very high intensity, short duration
  // VO2max intervals (Z5) - 70% of hard time
  workouts.push({
    type: 'interval',
    duration: weeklyHours * 0.14 * 3600, // 70% of 20%
    intensity: 5,
    tss: hardTSS * 0.70,
  });

  // Threshold (Z4) - 30% of hard time
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.06 * 3600, // 30% of 20%
    intensity: 4,
    tss: hardTSS * 0.30,
  });

  return workouts.filter(w => w.duration > 0);
}

/**
 * Distribute workouts for pyramidal training
 */
export function distributePyramidalWorkouts(weeklyTSS, weeklyHours) {
  const workouts = [];

  // More time in middle zones
  // Z1: 15%, Z2: 25%, Z3: 30%, Z4: 20%, Z5: 10%
  
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.15 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.10,
  });

  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.25 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.20,
  });

  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.30 * 3600,
    intensity: 3,
    tss: weeklyTSS * 0.35,
  });

  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.20 * 3600,
    intensity: 4,
    tss: weeklyTSS * 0.25,
  });

  workouts.push({
    type: 'interval',
    duration: weeklyHours * 0.10 * 3600,
    intensity: 5,
    tss: weeklyTSS * 0.10,
  });

  return workouts.filter(w => w.duration > 0);
}

/**
 * Distribute workouts for threshold training - Enhanced
 * Focus on Z3-Z4, minimal Z5
 */
export function distributeThresholdWorkouts(weeklyTSS, weeklyHours) {
  const workouts = [];

  // Minimal easy work
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.20 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.15,
  });

  // Z3: 40% (sweet spot)
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.40 * 3600,
    intensity: 3,
    tss: weeklyTSS * 0.35,
  });

  // Z4: 35% (threshold)
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.35 * 3600,
    intensity: 4,
    tss: weeklyTSS * 0.45,
  });

  // Minimal recovery
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.05 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.05,
  });

  // No Z5 intervals in threshold training

  return workouts.filter(w => w.duration > 0);
}

/**
 * Distribute workouts for sweet spot training - Enhanced
 * Z3-Z4 emphasis, time-efficient, no high-intensity intervals
 */
export function distributeSweetSpotWorkouts(weeklyTSS, weeklyHours) {
  const workouts = [];

  // Minimal easy work
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.25 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.20,
  });

  // Sweet spot (Z3.5) - 60% of time
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.60 * 3600,
    intensity: 3.5, // Between Z3 and Z4
    tss: weeklyTSS * 0.65,
  });

  // Minimal recovery
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.15 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.15,
  });

  // No high-intensity intervals

  return workouts.filter(w => w.duration > 0);
}

/**
 * Helper: Distribute by training style (existing logic)
 */
function distributeByTrainingStyle(weeklyTSS, weeklyHours, trainingStyle) {
  switch (trainingStyle) {
    case 'polarized':
      return distributePolarizedWorkouts(weeklyTSS, weeklyHours);
    case 'pyramidal':
      return distributePyramidalWorkouts(weeklyTSS, weeklyHours);
    case 'threshold':
      return distributeThresholdWorkouts(weeklyTSS, weeklyHours);
    case 'sweet-spot':
      return distributeSweetSpotWorkouts(weeklyTSS, weeklyHours);
    default:
      return distributePolarizedWorkouts(weeklyTSS, weeklyHours);
  }
}

/**
 * Strength-focused: More strength training, less pure endurance
 */
function distributeStrengthFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle) {
  const workouts = [];
  
  // 40% strength training
  workouts.push({
    type: 'strength',
    duration: weeklyHours * 0.4 * 3600,
    intensity: 4, // High intensity for strength
    tss: weeklyTSS * 0.35,
  });
  
  // 30% endurance (to maintain aerobic base)
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.3 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.25,
  });
  
  // 20% tempo (threshold work)
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.2 * 3600,
    intensity: 3.5,
    tss: weeklyTSS * 0.30,
  });

  // 10% recovery
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.1 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.10,
  });
  
  return workouts.filter(w => w.duration > 0);
}

/**
 * Speed-focused: More intervals and high-intensity work
 */
function distributeSpeedFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle) {
  const workouts = [];
  
  // 35% high-intensity intervals (Z5)
  workouts.push({
    type: 'interval',
    duration: weeklyHours * 0.35 * 3600,
    intensity: 5,
    tss: weeklyTSS * 0.45,
  });
  
  // 25% tempo (Z4)
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.25 * 3600,
    intensity: 4,
    tss: weeklyTSS * 0.30,
  });

  // 30% endurance (maintain base)
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.30 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.20,
  });
  
  // 10% recovery
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.10 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.05,
  });

  return workouts.filter(w => w.duration > 0);
}

/**
 * Skill-focused: Mix of skills practice and supporting workouts
 */
function distributeSkillFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle) {
  const workouts = [];

  // 30% skills practice
  workouts.push({
    type: 'skill',
    duration: weeklyHours * 0.30 * 3600,
    intensity: 2.5,
    tss: weeklyTSS * 0.25,
  });
  
  // 40% endurance (base for skills)
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.40 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.35,
  });
  
  // 20% tempo
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.20 * 3600,
    intensity: 3,
    tss: weeklyTSS * 0.30,
  });

  // 10% recovery
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.10 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.10,
  });
  
  return workouts.filter(w => w.duration > 0);
}

/**
 * Race-focused: Periodized approach with tapering (will be enhanced with tapering logic)
 */
function distributeRaceFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle, goal) {
  // Use speed-focused with more structure
  return distributeSpeedFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle);
}

/**
 * Weight-loss focused: Higher volume, moderate intensity
 */
function distributeWeightLossFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle) {
  const workouts = [];
  
  // 50% endurance (fat-burning zone)
  workouts.push({
    type: 'endurance',
    duration: weeklyHours * 0.50 * 3600,
    intensity: 2,
    tss: weeklyTSS * 0.40,
  });
  
  // 30% tempo (moderate intensity)
  workouts.push({
    type: 'tempo',
    duration: weeklyHours * 0.30 * 3600,
    intensity: 3,
    tss: weeklyTSS * 0.35,
  });
  
  // 10% intervals (metabolic boost)
  workouts.push({
    type: 'interval',
    duration: weeklyHours * 0.10 * 3600,
    intensity: 4.5,
    tss: weeklyTSS * 0.15,
  });
  
  // 10% recovery
  workouts.push({
    type: 'recovery',
    duration: weeklyHours * 0.10 * 3600,
    intensity: 1,
    tss: weeklyTSS * 0.10,
  });

  return workouts.filter(w => w.duration > 0);
}

/**
 * Distribute workouts based on primary goals and training style
 */
export function distributeWorkoutsByGoals(weeklyTSS, weeklyHours, goals = [], trainingStyle = 'polarized') {
  // Sort goals by priority
  const sortedGoals = [...goals].sort((a, b) => {
    const priorityOrder = { p1: 0, p2: 1, p3: 2 };
    return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
  });

  const primaryGoal = sortedGoals[0];
  
  // If no goals or primary goal is endurance, use standard distribution
  if (!primaryGoal || primaryGoal.type === 'endurance' || primaryGoal.type === 'general-fitness') {
    return distributeByTrainingStyle(weeklyTSS, weeklyHours, trainingStyle);
  }

  // Goal-specific distributions (strength removed; use event-driven mix or style for preparation)
  switch (primaryGoal.type) {
    case 'speed':
      return distributeSpeedFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle);
    
    case 'skill':
      return distributeSkillFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle);
    
    case 'race':
      return distributeRaceFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle, primaryGoal);
    
    case 'weight-loss':
      return distributeWeightLossFocusedWorkouts(weeklyTSS, weeklyHours, trainingStyle);
    
    default:
      return distributeByTrainingStyle(weeklyTSS, weeklyHours, trainingStyle);
  }
}

/**
 * Generate workout structure based on type and duration
 * Now includes warm-up and cool-down for ALL workouts
 */
export function generateWorkoutStructure(type, duration, intensity) {
  // Calculate warm-up and cool-down durations
  const getWarmupDuration = (totalDuration, workoutType) => {
    if (workoutType === 'interval' || workoutType === 'tempo') {
      return Math.min(900, totalDuration * 0.15); // 15% or max 15 min
    } else if (workoutType === 'endurance' && totalDuration > 3600) {
      return Math.min(600, totalDuration * 0.10); // 10% or max 10 min for long endurance
    } else if (workoutType === 'endurance') {
      return Math.min(300, totalDuration * 0.08); // 8% or max 5 min for shorter endurance
    } else {
      return Math.min(300, totalDuration * 0.10); // 10% or max 5 min default
    }
  };
  
  const getCooldownDuration = (totalDuration, workoutType) => {
    if (workoutType === 'interval' || workoutType === 'tempo') {
      return Math.min(600, totalDuration * 0.10); // 10% or max 10 min
    } else if (workoutType === 'endurance' && totalDuration > 3600) {
      return Math.min(600, totalDuration * 0.08); // 8% or max 10 min
    } else {
      return Math.min(300, totalDuration * 0.05); // 5% or max 5 min
    }
  };
  
  const warmupDuration = getWarmupDuration(duration, type);
  const cooldownDuration = getCooldownDuration(duration, type);
  const mainDuration = duration - warmupDuration - cooldownDuration;
  
  // Ensure main duration is positive
  if (mainDuration <= 0) {
    // If workout is too short, just use simple structure
    return {
      type: 'simple',
      simple: {
        duration,
        intensity,
      }
    };
  }

  // For interval workouts, use existing structure
  if (type === 'interval') {
    const workDuration = Math.min(300, mainDuration / 10);
    const restDuration = workDuration * 0.5;
    const intervalDuration = mainDuration;
    const repetitions = Math.floor(intervalDuration / (workDuration + restDuration));

    return {
      type: 'intervals',
      intervals: {
      warmup: {
        duration: warmupDuration,
        intensity: 1,
      },
      sets: [{
        workDuration,
        workIntensity: intensity,
        restDuration,
        restIntensity: 1,
          repetitions: Math.max(3, repetitions),
      }],
      cooldown: {
        duration: cooldownDuration,
        intensity: 1,
        }
      }
    };
  }
  
  // For tempo workouts longer than 1 hour, use pyramid structure
  if (type === 'tempo' && duration > 3600) {
    return {
      type: 'pyramid',
      pyramid: {
        warmup: {
          duration: warmupDuration,
          intensity: 2,
        },
        segments: [
          { duration: mainDuration * 0.15, intensity: 3 }, // Build
          { duration: mainDuration * 0.45, intensity: intensity }, // Main effort
          { duration: mainDuration * 0.15, intensity: 3 }, // Sustain
        ],
        cooldown: {
          duration: cooldownDuration,
          intensity: 1,
        }
      }
    };
  }
  
  // For all other workouts (endurance, recovery, strength, skill, etc.), use pyramid structure with warm-up/cool-down
  return {
    type: 'pyramid',
    pyramid: {
      warmup: {
        duration: warmupDuration,
        intensity: 1, // Always easy warm-up
      },
      segments: [
        {
          duration: mainDuration,
          intensity: intensity,
        }
      ],
      cooldown: {
        duration: cooldownDuration,
        intensity: 1, // Always easy cool-down
      }
    }
  };
}

/**
 * Assign priorities to workouts based on goals
 */
export function assignPriorities(workouts, goals = []) {
  // Sort goals by priority (p1 first)
  const sortedGoals = [...goals].sort((a, b) => {
    const priorityOrder = { p1: 0, p2: 1, p3: 2 };
    return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
  });

  // Priority distribution based on goals
  workouts.forEach((workout, index) => {
    // First workout of the week is usually P1
    if (index % 7 === 0 || workouts.length <= 3) {
      workout.priority = 'p1';
    } 
    // Hard workouts are P1 or P2
    else if (workout.intensity >= 4) {
      workout.priority = sortedGoals.length > 0 && sortedGoals[0].priority === 'p1' ? 'p1' : 'p2';
    }
    // Long endurance is P2 if goal is endurance
    else if (workout.type === 'endurance' && workout.duration > 7200) {
      const hasEnduranceGoal = sortedGoals.some(g => g.type === 'endurance');
      workout.priority = hasEnduranceGoal ? 'p2' : 'p3';
    }
    // Recovery is usually P3
    else if (workout.type === 'recovery') {
      workout.priority = 'p3';
    }
    // Default to P2
    else {
      workout.priority = 'p2';
    }
  });

  return workouts;
}

function mergeWorkouts(primary, extra) {
  // Merge "extra" into "primary" to avoid double sessions.
  // Training-science rationale: keep key sessions; fold low-intensity time into an easy day.
  const p = { ...primary };
  const addDuration = extra?.targetDuration || extra?.duration || 0;
  const addTSS = extra?.targetTSS || extra?.tss || 0;

  p.targetDuration = (p.targetDuration || p.duration || 0) + addDuration;
  p.targetTSS = (p.targetTSS || p.tss || 0) + addTSS;

  const addMin = Math.round(addDuration / 60);
  if (addMin > 0) {
    const extraType = extra?.type ? String(extra.type) : 'session';
    const note = `\n\nCombined: also include +${addMin} min easy (${extraType}).`;
    p.description = `${p.description || ''}${note}`.trim();
  }

  // Keep existing structure; mark as composite for future use if desired
  p.structure = p.structure || primary.structure;
  p.combined = true;
  return p;
}

function consolidateForSingleSessionPerDay(weekWorkouts, maxDays) {
  if (!Array.isArray(weekWorkouts)) return [];
  if (weekWorkouts.length <= maxDays) return weekWorkouts;

  const list = [...weekWorkouts];

  // Prioritize: hard sessions (>=4) + highest priority first; keep these distinct.
  const priorityOrder = { p1: 0, p2: 1, p3: 2 };
  list.sort((a, b) => {
    const pa = priorityOrder[a?.priority] ?? 2;
    const pb = priorityOrder[b?.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    const ia = a?.intensity || a?.targetIntensity?.zone || 0;
    const ib = b?.intensity || b?.targetIntensity?.zone || 0;
    return ib - ia;
  });

  const kept = [];
  const extras = [];

  for (const w of list) {
    if (kept.length < maxDays) kept.push(w);
    else extras.push(w);
  }

  // Merge extras (prefer merging recovery/endurance into the easiest kept workout)
  const keptByEase = [...kept].sort((a, b) => {
    const ia = a?.intensity || a?.targetIntensity?.zone || 0;
    const ib = b?.intensity || b?.targetIntensity?.zone || 0;
    return ia - ib;
  });

  for (const extra of extras) {
    const extraType = String(extra?.type || '');
    const isEasyExtra = extraType === 'recovery' || extraType === 'endurance' || (extra?.intensity || 0) <= 2;

    const target = isEasyExtra
      ? keptByEase[0] // merge into easiest day
      : keptByEase[keptByEase.length - 1]; // last resort: merge into hardest day (rare)

    const idx = kept.findIndex((k) => k === target);
    kept[idx] = mergeWorkouts(target, extra);
    // refresh ease ordering
    keptByEase.splice(0, keptByEase.length, ...[...kept].sort((a, b) => {
      const ia = a?.intensity || a?.targetIntensity?.zone || 0;
      const ib = b?.intensity || b?.targetIntensity?.zone || 0;
      return ia - ib;
    }));
  }

  return kept;
}

/**
 * Load user's current fitness metrics (CTL, ATL, TSB)
 */
export async function loadUserFitnessData(userId) {
  try {
    // Try to get from user profile first
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();
      if (data.currentCTL !== undefined) {
        return {
          ctl: data.currentCTL || 50,
          atl: data.currentATL || 50,
          tsb: data.currentTSB || 0,
        };
      }
    }
    
    // If not in profile, calculate from recent daily metrics
    const metricsRef = collection(db, 'dailyMetrics');
    const q = query(
      metricsRef,
      where('userId', '==', userId),
      orderBy('date', 'desc'),
      limit(42) // Last 42 days for CTL
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      // No data, return defaults
      return { ctl: 50, atl: 50, tsb: 0 };
    }
    
    // Calculate CTL and ATL from historical data
    let ctl = 50; // Start with default
    let atl = 50;
    
    const metrics = [];
    snapshot.forEach(doc => {
      metrics.push({ ...doc.data(), id: doc.id });
    });
    
    // Sort chronologically (oldest first)
    metrics.reverse();
    
    // Calculate CTL (42-day average)
    metrics.forEach(metric => {
      ctl = calculateCTL(ctl, metric.totalTSS || 0, 42);
    });
    
    // Calculate ATL (7-day average) from last 7 days
    const last7Days = metrics.slice(-7);
    last7Days.forEach(metric => {
      atl = calculateATL(atl, metric.totalTSS || 0, 7);
    });
    
    const tsbResult = calculateTSB(ctl, atl);
    
    return { ctl, atl, tsb: tsbResult.tsb };
  } catch (error) {
    console.error('Error loading user fitness data:', error);
    return { ctl: 50, atl: 50, tsb: 0 }; // Default fallback
  }
}

/** Default FTP/maxHR used when user has no profile zones (reasonable averages for cycling). */
const DEFAULT_FTP = 200;
const DEFAULT_MAX_HR = 190;

/**
 * Load user training zones (power and HR) for plan generation.
 * Uses trainingZones collection (userId + sport); falls back to user profile ftp/maxHR to compute zones;
 * if none, returns default zones from average values (DEFAULT_FTP, DEFAULT_MAX_HR).
 * @returns {{ powerZones: object | null, hrZones: object | null }}
 */
export async function loadUserTrainingZones(userId, sport = 'cycling') {
  try {
    const zonesRef = collection(db, 'trainingZones');
    const q = query(
      zonesRef,
      where('userId', '==', userId),
      where('sport', '==', sport)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      return {
        powerZones: data.powerZones || null,
        hrZones: data.hrZones || (data.zone1 && !data.powerZones ? data : null),
      };
    }
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const u = userDoc.data();
      const ftp = u.ftp && u.ftp > 0 ? u.ftp : (u.maxPower && u.maxPower > 0 ? estimateFTPFromMaxPower(u.maxPower) : null);
      const maxHR = u.maxHR && u.maxHR > 0 ? u.maxHR : (u.age && u.age > 0 ? estimateMaxHRFromAge(u.age) : null);
      return {
        powerZones: ftp && ftp > 0 ? calculatePowerZonesFromFTP(ftp) : null,
        hrZones: maxHR && maxHR > 0 ? calculateHRZonesFromMax(maxHR) : null,
      };
    }
  } catch (error) {
    console.error('Error loading user training zones:', error);
  }
  return {
    powerZones: calculatePowerZonesFromFTP(DEFAULT_FTP),
    hrZones: calculateHRZonesFromMax(DEFAULT_MAX_HR),
  };
}

/**
 * Get power and HR range for a zone (1–5) from zones objects.
 * Used to attach target ranges to generated workouts.
 */
function getZoneRangesForIntensity(zoneNum, powerZones, hrZones) {
  const z = Math.min(5, Math.max(1, Math.round(zoneNum)));
  const key = `zone${z}`;
  const targetPowerRange =
    powerZones && powerZones[key]
      ? { min: powerZones[key].min, max: powerZones[key].max }
      : null;
  const targetHRRange =
    hrZones && hrZones[key]
      ? { min: hrZones[key].min, max: hrZones[key].max }
      : null;
  return { targetPowerRange, targetHRRange };
}

/**
 * Apply tapering logic before race dates
 * Standard taper: 3 weeks out (reduce volume 20%), 2 weeks out (reduce 40%), 1 week out (reduce 60%)
 */
export function applyTaperingLogic(workouts, raceDate, startDate) {
  if (!raceDate) return workouts;
  
  const race = raceDate instanceof Date ? raceDate : raceDate.toDate();
  const start = new Date(startDate);
  
  workouts.forEach(workout => {
    const workoutDate = workout.scheduledDate?.toDate() || 
                       new Date(start.getTime() + (workout.weekNumber * 7 * 24 * 60 * 60 * 1000));
    
    const daysUntilRace = Math.floor((race - workoutDate) / (1000 * 60 * 60 * 24));
    
    // Taper logic
    if (daysUntilRace >= 0 && daysUntilRace <= 21) {
      let volumeReduction = 0;
      
      if (daysUntilRace <= 7) {
        // Final week: 60% reduction
        volumeReduction = 0.6;
      } else if (daysUntilRace <= 14) {
        // 2 weeks out: 40% reduction
        volumeReduction = 0.4;
      } else if (daysUntilRace <= 21) {
        // 3 weeks out: 20% reduction
        volumeReduction = 0.2;
      }
      
      // Reduce duration and TSS
      workout.targetDuration = Math.round(workout.targetDuration * (1 - volumeReduction));
      workout.targetTSS = Math.round(workout.targetTSS * (1 - volumeReduction));
      
      // Reduce intensity in final week (more recovery)
      if (daysUntilRace <= 7 && workout.type !== 'recovery') {
        workout.targetIntensity.zone = Math.max(1, workout.targetIntensity.zone - 1);
      }
      
      // Mark as taper week
      workout.taperWeek = true;
    }
    
    // Remove hard workouts in final 3 days before race
    if (daysUntilRace >= 0 && daysUntilRace <= 3) {
      if (workout.intensity >= 4 || workout.type === 'interval') {
        workout.type = 'recovery';
        workout.targetIntensity.zone = 1;
        workout.targetDuration = Math.min(workout.targetDuration, 1800); // Max 30 min
        workout.targetTSS = Math.round(workout.targetTSS * 0.3);
      }
    }
  });
  
  return workouts;
}

/**
 * Detect events from user's calendar (events use dayKey YYYY-MM-DD)
 * Returns full event data including competition (raceTypeId, effortProfile, priority) for plan bias.
 */
export async function detectCalendarEvents(userId, startDate, endDate) {
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startKey = start.toISOString().slice(0, 10); // YYYY-MM-DD
    const endKey = end.toISOString().slice(0, 10);

    const eventsRef = collection(db, 'events');
    const q = query(
      eventsRef,
      where('userId', '==', userId),
      where('dayKey', '>=', startKey),
      where('dayKey', '<=', endKey)
    );

    const snapshot = await getDocs(q);
    const events = [];

    snapshot.forEach((d) => {
      const data = d.data();
      const dayKey = data.dayKey || startKey;
      const eventDate = new Date(dayKey + 'T12:00:00');
      events.push({
        id: d.id,
        date: eventDate,
        dayKey,
        name: data.title || data.name || 'Event',
        type: data.type || 'event',
        competition: data.competition || null, // { raceTypeId, raceTypeName, priority, effortProfile }
      });
    });

    return events;
  } catch (error) {
    console.error('Error detecting calendar events:', error);
    return [];
  }
}

/**
 * Compute target zone mix (Z1–Z5) from upcoming events, weighted by priority (A/B/C).
 * Used to tailor training so the athlete is in best form for those specific events.
 * Returns { z1, z2, z3, z4, z5 } as fractions (sum 1), or null if no events with effortProfile.
 */
export function computeEventDrivenZoneMix(eventsWithCompetition) {
  const comps = (eventsWithCompetition || []).filter((e) => e.competition && e.competition.effortProfile?.zoneMix);
  if (comps.length === 0) return null;

  let totalWeight = 0;
  const sum = { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 };
  comps.forEach((e) => {
    const w = e.competition.priority === 'A' ? 1.5 : e.competition.priority === 'B' ? 1 : 0.5;
    const zm = e.competition.effortProfile.zoneMix;
    totalWeight += w;
    sum.z1 += (Number(zm.z1) || 0) / 100 * w;
    sum.z2 += (Number(zm.z2) || 0) / 100 * w;
    sum.z3 += (Number(zm.z3) || 0) / 100 * w;
    sum.z4 += (Number(zm.z4) || 0) / 100 * w;
    sum.z5 += (Number(zm.z5) || 0) / 100 * w;
  });
  if (totalWeight <= 0) return null;
  const raw = {
    z1: sum.z1 / totalWeight,
    z2: sum.z2 / totalWeight,
    z3: sum.z3 / totalWeight,
    z4: sum.z4 / totalWeight,
    z5: sum.z5 / totalWeight,
  };
  const total = raw.z1 + raw.z2 + raw.z3 + raw.z4 + raw.z5;
  if (total <= 0) return null;
  return {
    z1: raw.z1 / total,
    z2: raw.z2 / total,
    z3: raw.z3 / total,
    z4: raw.z4 / total,
    z5: raw.z5 / total,
  };
}

/**
 * Apply science-based floor: we cannot prepare in isolation to one zone.
 * Minimum ~55% easy (Z1+Z2) to maintain aerobic base; remaining time follows event mix for Z3/Z4/Z5.
 */
export function applyEasyFloor(zoneMix, easyFloor = 0.55) {
  if (!zoneMix) return null;
  const easy = zoneMix.z1 + zoneMix.z2;
  if (easy >= easyFloor) return { ...zoneMix };
  const hardShare = 1 - easyFloor;
  const hardTotal = zoneMix.z3 + zoneMix.z4 + zoneMix.z5;
  const easyRatio = easy > 0 ? easy : 1;
  const z1 = (zoneMix.z1 / easyRatio) * easyFloor;
  const z2 = (zoneMix.z2 / easyRatio) * easyFloor;
  if (hardTotal <= 0) {
    return { z1: 0.20, z2: 0.35, z3: 0.25, z4: 0.15, z5: 0.05 };
  }
  return {
    z1,
    z2,
    z3: (zoneMix.z3 / hardTotal) * hardShare,
    z4: (zoneMix.z4 / hardTotal) * hardShare,
    z5: (zoneMix.z5 / hardTotal) * hardShare,
  };
}

/**
 * Distribute weekly workouts from an event-driven zone mix (Z1–Z5).
 * Science: respects easy floor; emphasizes zones that matter for the target events.
 * Maps zones to workout types: Z1→recovery, Z2→endurance, Z3→tempo, Z4→tempo(4), Z5→interval.
 */
function distributeWorkoutsByEventZoneMix(zoneMix, weeklyTSS, weeklyHours) {
  if (!zoneMix || weeklyHours <= 0) return distributePolarizedWorkouts(weeklyTSS, weeklyHours);
  const totalSec = weeklyHours * 3600;
  const workouts = [];
  const push = (type, intensity, fraction) => {
    const duration = Math.round(totalSec * fraction);
    if (duration < 300) return;
    const tss = Math.round(weeklyTSS * fraction);
    workouts.push({ type, duration, intensity, tss });
  };
  push('recovery', 1, zoneMix.z1);
  push('endurance', 2, zoneMix.z2);
  push('tempo', 3, zoneMix.z3);
  push('tempo', 4, zoneMix.z4);
  push('interval', 5, zoneMix.z5);
  const out = workouts.filter((w) => w.duration > 0);
  if (out.length === 0) return distributePolarizedWorkouts(weeklyTSS, weeklyHours);
  return out;
}

/**
 * Infer training bias from calendar events (race types + priority).
 * Returns a suggested primary goal type, weight, and eventPriority for blending with user goals.
 * High Z4/Z5 share → speed; long duration + Z2/Z3 → endurance; mix → keep balanced.
 * eventPriority: p1 if any A race, p2 if any B, else p3 — so the plan can be tailored by event importance.
 */
export function inferEventMixBias(eventsWithCompetition) {
  const comps = (eventsWithCompetition || []).filter((e) => e.competition && e.competition.effortProfile);
  if (comps.length === 0) return null;

  let totalWeight = 0;
  let sumZ4Z5 = 0;
  let sumDuration = 0;
  let count = 0;
  let eventPriority = 'p3';
  comps.forEach((e) => {
    const p = e.competition.priority === 'A' ? 1.5 : e.competition.priority === 'B' ? 1 : 0.5;
    if (e.competition.priority === 'A') eventPriority = 'p1';
    else if (e.competition.priority === 'B' && eventPriority !== 'p1') eventPriority = 'p2';
    const zm = e.competition.effortProfile?.zoneMix || {};
    const z4 = (zm.z4 || 0) / 100;
    const z5 = (zm.z5 || 0) / 100;
    const dur = e.competition.effortProfile?.durationMinutes || 60;
    totalWeight += p;
    sumZ4Z5 += (z4 + z5) * p;
    sumDuration += dur * p;
    count += 1;
  });

  if (count === 0) return null;
  const avgZ4Z5 = sumZ4Z5 / totalWeight;
  const avgDuration = sumDuration / totalWeight;

  if (avgZ4Z5 >= 0.5 && avgDuration <= 90) return { suggestedGoal: 'speed', weight: 0.6, eventPriority };
  if (avgDuration >= 180 && avgZ4Z5 <= 0.2) return { suggestedGoal: 'endurance', weight: 0.6, eventPriority };
  if (avgZ4Z5 >= 0.35) return { suggestedGoal: 'speed', weight: 0.4, eventPriority };
  return null;
}

/** Types that are "really different" and can share the same day (e.g. skills + endurance). */
const COMPLEMENTARY_TYPES = [
  ['skill', 'endurance'],
  ['skill', 'recovery'],
];

function areReallyDifferentTypes(types) {
  if (!Array.isArray(types) || types.length < 2) return false;
  const set = new Set(types.map((t) => String(t).toLowerCase()));
  return COMPLEMENTARY_TYPES.some(
    ([a, b]) => set.has(a) && set.has(b)
  );
}

/**
 * Enforce one workout per day. If a day has multiple workouts, merge them into one
 * unless they are "really different" (e.g. skills + endurance), in which case keep both with different times.
 * Returns the filtered array (merged duplicates removed).
 */
function enforceOneWorkoutPerDay(workouts) {
  const dayKey = (w) => {
    const d = w.scheduledDate?.toDate ? w.scheduledDate.toDate() : w.scheduledDate;
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    return date.toISOString().slice(0, 10);
  };
  const byDay = new Map();
  workouts.forEach((w) => {
    const key = dayKey(w);
    if (!key) return;
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(w);
  });

  const toRemove = new Set();
  byDay.forEach((group, key) => {
    if (group.length <= 1) return;
    const types = group.map((w) => w.type);
    if (areReallyDifferentTypes(types)) {
      // Allow two on same day: set different times (e.g. 9:00 and 14:00)
      group.forEach((w, i) => {
        const d = w.scheduledDate?.toDate ? w.scheduledDate.toDate() : new Date(w.scheduledDate);
        d.setHours(i === 0 ? 9 : 14, 0, 0, 0);
        w.scheduledDate = Timestamp.fromDate(d);
        w.scheduledTime = i === 0 ? '09:00' : '14:00';
      });
      return;
    }
    // Same day has similar workouts (e.g. two endurance): keep one, merge rest, mark rest for removal
    const priorityOrder = { p1: 0, p2: 1, p3: 2 };
    group.sort((a, b) => {
      const pa = priorityOrder[a.priority] ?? 2;
      const pb = priorityOrder[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return (b.intensity || 0) - (a.intensity || 0);
    });
    const keeper = group[0];
    for (let i = 1; i < group.length; i++) {
      const extra = group[i];
      const merged = mergeWorkouts(keeper, extra);
      Object.assign(keeper, merged);
      toRemove.add(extra);
    }
  });

  return workouts.filter((w) => !toRemove.has(w));
}

/**
 * Optimize workout schedule based on preferred days and times.
 * One workout per day by default; exception: same day can have two only when really different (e.g. skills + endurance).
 * Now includes event awareness.
 */
export function optimizeSchedule(workouts, startDate, preferredDays = [0, 1, 2, 3, 4, 5, 6], preferredTimes = {}, events = []) {
  const start = new Date(startDate);
  
  // Group workouts by week
  const workoutsByWeek = new Map();
  workouts.forEach(w => {
    const weekNum = w.weekNumber || 0;
    if (!workoutsByWeek.has(weekNum)) {
      workoutsByWeek.set(weekNum, []);
    }
    workoutsByWeek.get(weekNum).push(w);
  });

  // Schedule each week — aim for one session per day (consolidate so we never exceed available days)
  workoutsByWeek.forEach((weekWorkouts, weekNum) => {
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() + (weekNum * 7));

    // Sort workouts by priority and intensity (hard workouts first)
    weekWorkouts.sort((a, b) => {
      const priorityOrder = { p1: 0, p2: 1, p3: 2 };
      const priorityDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
      if (priorityDiff !== 0) return priorityDiff;
      return (b.intensity || 0) - (a.intensity || 0);
    });

    // Get available days (excluding event days)
    const scheduledDays = Array.isArray(preferredDays) && preferredDays.length > 0 
      ? [...preferredDays] 
      : [0, 1, 2, 3, 4, 5, 6];
    
    // Remove days with events
    const availableDays = scheduledDays.filter(day => {
      const dayDate = new Date(weekStart);
      dayDate.setDate(dayDate.getDate() + day);
      return !events.some(event => {
        const eventDate = event.date instanceof Date ? event.date : event.date.toDate();
        return eventDate.toDateString() === dayDate.toDateString();
      });
    });
    
    // One workout per day: cap sessions at number of available days (merge extras)
    const uniqueDays = availableDays.length > 0 
      ? Array.from(new Set(availableDays)).sort((a, b) => a - b)
      : Array.from(new Set(scheduledDays)).sort((a, b) => a - b);
    
    const maxSessionsPerWeek = uniqueDays.length;
    const finalWeekWorkouts = consolidateForSingleSessionPerDay(weekWorkouts, maxSessionsPerWeek);

    // Distribute across available days (one workout per day)
    let dayIndex = 0;
    finalWeekWorkouts.forEach((workout, index) => {
      let scheduledDay = uniqueDays[dayIndex % uniqueDays.length];
      
      // Check if this day has an event
      const workoutDate = new Date(weekStart);
      workoutDate.setDate(workoutDate.getDate() + scheduledDay);
      
      const hasEvent = events.some(event => {
        const eventDate = event.date instanceof Date ? event.date : event.date.toDate();
        return eventDate.toDateString() === workoutDate.toDateString();
      });
      
      if (hasEvent) {
        // Convert to easy recovery on event day
        workout.type = 'recovery';
        workout.targetIntensity.zone = 1;
        workout.targetDuration = Math.min(workout.targetDuration, 1800); // Max 30 min
        workout.targetTSS = Math.round(workout.targetTSS * 0.3);
        workout.eventDay = true;
      }
      
      // Ensure recovery days after hard workouts
      if (workout.intensity >= 4 && index > 0 && !hasEvent) {
        dayIndex++;
        scheduledDay = uniqueDays[dayIndex % uniqueDays.length];
      }

      const finalWorkoutDate = new Date(weekStart);
      finalWorkoutDate.setDate(finalWorkoutDate.getDate() + scheduledDay);
      
      // Set preferred time if available
      const timeSlot = preferredTimes.morning || preferredTimes.afternoon || preferredTimes.evening;
      if (timeSlot) {
        const [hours, minutes] = timeSlot.start.split(':').map(Number);
        finalWorkoutDate.setHours(hours, minutes, 0, 0);
        workout.scheduledTime = timeSlot.start;
      } else {
        finalWorkoutDate.setHours(10, 0, 0, 0); // Default 10 AM
      }

      workout.scheduledDate = Timestamp.fromDate(finalWorkoutDate);
      workout.dayOfWeek = scheduledDay;
      
      dayIndex++;
    });
  });

  // Enforce one workout per day: merge any same-day duplicates unless really different (e.g. skills + endurance)
  return enforceOneWorkoutPerDay(workouts);
}

/**
 * Generate complete training plan
 * Now async to support loading user fitness data and calendar events
 */
export async function generateTrainingPlan(config) {
  const {
    startDate,
    endDate,
    weeklyHours,
    periodization = 'linear',
    trainingStyle = 'polarized',
    sport = 'cycling',
    goals = [],
    preferredDays = [0, 1, 2, 3, 4, 5, 6],
    preferredTimes = {},
    currentCTL,
    userId, // Add userId for loading fitness data and events
  } = config;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalWeeks = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 7));

  // Load actual fitness data if userId provided
  let actualCTL = currentCTL || 50;
  if (userId && !currentCTL) {
    try {
      const fitnessData = await loadUserFitnessData(userId);
      actualCTL = fitnessData.ctl;
    } catch (error) {
      console.error('Error loading fitness data, using default:', error);
    }
  }

  // Load user training zones (power/HR) for workout target ranges; fallback to default averages if none
  let powerZones = null;
  let hrZones = null;
  if (userId) {
    try {
      const zones = await loadUserTrainingZones(userId, sport);
      powerZones = zones.powerZones;
      hrZones = zones.hrZones;
    } catch (error) {
      console.error('Error loading user training zones, using defaults:', error);
      powerZones = calculatePowerZonesFromFTP(DEFAULT_FTP);
      hrZones = calculateHRZonesFromMax(DEFAULT_MAX_HR);
    }
  }
  if (!powerZones) powerZones = calculatePowerZonesFromFTP(DEFAULT_FTP);
  if (!hrZones) hrZones = calculateHRZonesFromMax(DEFAULT_MAX_HR);

  // Detect calendar events (within plan) and upcoming events (up to 8 weeks after plan end) for race-specific mix
  let calendarEvents = [];
  let upcomingEventsForMix = [];
  if (userId) {
    try {
      calendarEvents = await detectCalendarEvents(userId, startDate, endDate);
      const endExtended = new Date(end);
      endExtended.setDate(endExtended.getDate() + 56); // +8 weeks
      upcomingEventsForMix = await detectCalendarEvents(userId, startDate, endExtended);
    } catch (error) {
      console.error('Error detecting calendar events:', error);
    }
  }

  // Event-driven zone mix: from upcoming races (priority + race type) to target training distribution
  const rawEventMix = computeEventDrivenZoneMix(upcomingEventsForMix);
  const eventDrivenZoneMix = rawEventMix ? applyEasyFloor(rawEventMix, 0.55) : null;

  // Race dates for tapering: from goals (if any) and from calendar events with competition
  const fromGoals = goals
    .filter(g => g.type === 'race' && g.target?.date)
    .map(g => {
      const date = g.target.date;
      return date instanceof Date ? date : date.toDate();
    });
  const fromCalendar = calendarEvents
    .filter(e => e.competition && e.date)
    .map(e => (e.date instanceof Date ? e.date : e.date.toDate()));
  const raceDates = [...new Set([...fromGoals, ...fromCalendar])].sort((a, b) => a - b);

  // Merge race goals with calendar events (calendar events have .date and .competition)
  const allEvents = [
    ...raceDates.map(rd => ({ date: rd, type: 'race', name: 'Race', competition: null })),
    ...calendarEvents,
  ].sort((a, b) => {
    const dateA = a.date instanceof Date ? a.date : a.date.toDate();
    const dateB = b.date instanceof Date ? b.date : b.date.toDate();
    return dateA - dateB;
  });

  // When we have upcoming races with effort profile, event-driven zone mix drives the workout distribution.
  const eventMixBias = inferEventMixBias(calendarEvents);
  const goalsWithEventBias =
    eventMixBias?.suggestedGoal
      ? [{ type: eventMixBias.suggestedGoal, priority: eventMixBias.eventPriority || 'p2' }, ...goals]
      : goals;

  // Determine phases
  const phases = determinePhases(startDate, endDate, periodization);

  // Generate workouts for each phase
  const allWorkouts = [];

  phases.forEach((phase) => {
    const phaseWeeklyTSS = calculateWeeklyTSS(weeklyHours, actualCTL, phase.type);

    // Prefer event-driven zone mix (upcoming races × priority) so plan targets best form for those events
    const workoutTemplates = eventDrivenZoneMix
      ? distributeWorkoutsByEventZoneMix(eventDrivenZoneMix, phaseWeeklyTSS, weeklyHours)
      : distributeWorkoutsByGoals(
          phaseWeeklyTSS,
          weeklyHours,
          goalsWithEventBias,
          trainingStyle
        );

    // Generate workouts for each week in phase
    for (let week = 0; week < phase.weeks; week++) {
      const weekNum = phase.startWeek + week;
      const weekStart = new Date(start);
      weekStart.setDate(weekStart.getDate() + (weekNum * 7));

      workoutTemplates.forEach((template, index) => {
        const structure = generateWorkoutStructure(template.type, template.duration, template.intensity);
        const targetTSS = estimateWorkoutTSS(template.duration, template.intensity);
        const zoneNum = Math.round(template.intensity);
        const { targetPowerRange, targetHRRange } = getZoneRangesForIntensity(zoneNum, powerZones, hrZones);

        allWorkouts.push({
          type: template.type,
          sport,
          name: getWorkoutName(template.type, template.intensity),
          description: getWorkoutDescription(template.type, template.intensity, template.duration, structure),
          structure,
          targetDuration: template.duration,
          targetTSS,
          targetIntensity: {
            zone: zoneNum,
          },
          ...(targetPowerRange && { targetPowerRange }),
          ...(targetHRRange && { targetHRRange }),
          intensity: template.intensity, // Add intensity for priority assignment
          priority: 'p2', // Will be assigned later
          status: 'planned',
          weekNumber: weekNum,
          autoGenerated: true,
          createdAt: Timestamp.now(),
        });
      });
    }
  });

  // Apply tapering before races
  raceDates.forEach(raceDate => {
    applyTaperingLogic(allWorkouts, raceDate, startDate);
  });

  // Assign priorities based on goals (including event-driven bias so A/B/C races influence key sessions)
  assignPriorities(allWorkouts, goalsWithEventBias);

  // Optimize schedule: one workout per day (exception: really different types e.g. skills + endurance)
  const scheduledWorkouts = optimizeSchedule(allWorkouts, startDate, preferredDays, preferredTimes, allEvents);

  return scheduledWorkouts;
}

/**
 * Get workout name based on type and intensity
 */
function getWorkoutName(type, intensity) {
  const names = {
    endurance: `Endurance Zone ${Math.round(intensity)}`,
    tempo: `Tempo Zone ${Math.round(intensity)}`,
    interval: `Interval Zone ${Math.round(intensity)}`,
    recovery: 'Recovery Ride',
    strength: 'Strength Training',
    skill: 'Skills Practice',
    mental: 'Mental Training',
    rest: 'Rest Day',
  };
  return names[type] || `${type.charAt(0).toUpperCase() + type.slice(1)} Workout`;
}

/**
 * Get workout description
 */
function getWorkoutDescription(type, intensity, duration, structure) {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const durationStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  const zone = Math.round(intensity);
  const header = `${type.charAt(0).toUpperCase() + type.slice(1)} — Zone ${zone} — ${durationStr}`;

  const goalByType = {
    endurance: 'Build aerobic base with steady effort.',
    recovery: 'Promote recovery: keep it very easy.',
    tempo: 'Sustain moderately hard effort; build stamina.',
    interval: 'High-intensity work with full quality reps.',
    strength: 'Strength and stability focus.',
    skill: 'Technique and efficiency focus.',
    mental: 'Mental skills focus.',
    race: 'Race-specific preparation.',
    rest: 'Rest and recover.',
    test: 'Assess fitness.',
    custom: 'Custom session.',
  };

  const howTo = [];
  howTo.push(`Goal: ${goalByType[type] || 'Complete the session as planned.'}`);

  // Build steps from structure when possible
  if (structure?.type === 'intervals' && structure?.intervals) {
    const wu = structure.intervals.warmup;
    const cd = structure.intervals.cooldown;
    const set0 = structure.intervals.sets?.[0];
    if (wu?.duration) {
      howTo.push(`Warm-up: ${Math.round(wu.duration / 60)} min easy (Zone ${wu.intensity || 1}).`);
    }
    if (set0) {
      const reps = set0.repetitions || 0;
      const workMin = Math.round((set0.workDuration || 0) / 60);
      const restMin = Math.round((set0.restDuration || 0) / 60);
      howTo.push(`Main set: ${reps} × ${workMin} min in Zone ${zone} with ${restMin} min easy between reps.`);
    }
    if (cd?.duration) {
      howTo.push(`Cool-down: ${Math.round(cd.duration / 60)} min easy (Zone ${cd.intensity || 1}).`);
    }
  } else if (structure?.type === 'pyramid' && structure?.pyramid) {
    const wu = structure.pyramid.warmup;
    const cd = structure.pyramid.cooldown;
    const segments = structure.pyramid.segments;
    
    if (wu?.duration) {
      howTo.push(`Warm-up: ${Math.round(wu.duration / 60)} min easy (Zone ${wu.intensity || 1}).`);
    }
    
    if (segments && segments.length > 0) {
      if (segments.length === 1) {
        // Simple structure
        const mainMin = Math.round(segments[0].duration / 60);
        howTo.push(`Main effort: ${mainMin} min in Zone ${Math.round(segments[0].intensity)}.`);
      } else {
        // Multiple segments
        segments.forEach((seg, idx) => {
          const segMin = Math.round(seg.duration / 60);
          howTo.push(`Segment ${idx + 1}: ${segMin} min in Zone ${Math.round(seg.intensity)}.`);
        });
      }
    }
    
    if (cd?.duration) {
      howTo.push(`Cool-down: ${Math.round(cd.duration / 60)} min easy (Zone ${cd.intensity || 1}).`);
    }
    
    // Add execution tips
    if (type === 'recovery') {
      howTo.push('Execution: keep cadence smooth, stay relaxed, avoid hard efforts.');
    } else if (type === 'endurance') {
      howTo.push('Execution: steady pace; you should be able to talk in full sentences.');
    } else if (type === 'tempo') {
      howTo.push('Execution: controlled discomfort; you can speak short phrases.');
    }
  } else {
    // Fallback for simple structure - still mention warm-up/cool-down
    howTo.push('Warm-up: 5-10 min easy.');
    if (type === 'recovery') {
      howTo.push('Execution: keep cadence smooth, stay relaxed, avoid hard efforts.');
    } else if (type === 'endurance') {
      howTo.push('Execution: steady pace; you should be able to talk in full sentences.');
    } else if (type === 'tempo') {
      howTo.push('Execution: controlled discomfort; you can speak short phrases.');
    } else {
      howTo.push(`Execution: hold Zone ${zone} as consistently as possible for most of the session.`);
    }
    howTo.push('Cool-down: 5-10 min easy.');
  }

  return `${header}\n\n${howTo.join('\n')}`;
}
